import { useCallback, useEffect, useState } from 'react';
import { Divider, Flex, Text } from '@chakra-ui/react';
import {
  Accordion,
  Badge,
  Button,
  ButtonText,
  Checkbox,
  useToast
} from '@morressier/alembic';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Utils } from '@publishing-engine/editorial-form-field-build/src';
import { IEditorialFormField } from '@publishing-engine/domain/models';

import AddNoteModal from 'src/components/SubmissionDetailsSidebar/AddNoteModal';
import ProtectedAction from 'src/components/ProtectedAction';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { ERRORS, SELECTED_ITEM_QUERY_KEY } from 'src/constants';
import { useUpdateEditorialFormData } from 'src/api/commands';

import withAuthorizedComponent from 'src/components/AuthorizedComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCheck,
  faEnvelope,
  faFileImport,
  faPencil,
  faWarning
} from '@fortawesome/pro-regular-svg-icons';
import useEditorialForm from 'src/hooks/useEditorialForm';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { EditorialAuthorWithdrawalRequested } from '../common/WithdrawalRequested';
import { IconBadge } from 'src/components/IconBadge';
import AdditionalInformationWrapper from '../../AdditionalInformationWrapper';
import HighlightItem from '../PeerReview/HighlightItem';
import { useQuery } from '@tanstack/react-query';
import { fetchExpertsInviteDetails } from 'src/api/queries';
import { SUBMISSION_DETAIL_TABS } from 'src/pages/SubmissionDetails';
import { useSubmissionDetails } from 'src/pages/SubmissionDetails/SubmissionDetailsContext';
import { getCheckListName } from 'src/utils/string';
import ProtectedWorkflowAction from '../../../../ProtectedAction/ProtectedWorkflowAction';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { FieldLabel } from 'src/components/Field';

type CheckListCopy = {
  heading: string;
  notesModalName: string;
  buttonText: string;
};

interface Props {
  setActiveTab: (tab: any) => JSX.Element;
}
const InitialAssessmentAdditionalInfo = ({ setActiveTab }: Props) => {
  // state
  const [showAddNoteModal, setShowAddNoteModal] = useState<string | null>(null);

  // hooks
  const toast = useToast();
  const ctxId = useCurrentContextId() ?? '';
  const {
    workflowResponse,
    submissionContextId,
    refetchSubmission,
    isDecisionMaker
  } = useSubmissionData();
  const { submissionContext } = useSubmissionContextData();
  const { organizationId, id: articleWorkflowId } =
    workflowResponse.articleWorkflow;
  const { setReviewsSubTab } = useSubmissionDetails();
  const { submissionId: paramId = '' } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const selectedItem = searchParams.get(SELECTED_ITEM_QUERY_KEY);
  const submissionId = selectedItem || paramId;
  const fetchInitialAssessmentDataKey = [
    'submission-checklist',
    { submissionId, phase: 'initialAssessment', articleWorkflowId }
  ];
  const {
    editorialFormSchema: initialAssessmentFormSchema,
    editorialFormData: initialAssessmentData,
    defaultFormValues
  } = useEditorialForm(
    submissionId,
    ctxId,
    organizationId,
    'initialAssessment',
    articleWorkflowId
  );

  const fieldNames =
    initialAssessmentFormSchema?.formFields?.map(field => field.name) ?? [];

  const isCompleteChecklistDisabled =
    fieldNames.length > 0 &&
    fieldNames.every(name => initialAssessmentData?.caseData?.[name] === true);

  const updateChecklistMutation = useUpdateEditorialFormData(
    submissionId,
    ctxId,
    articleWorkflowId,
    'initialAssessment',
    fetchInitialAssessmentDataKey,
    organizationId
  );

  const { reset, register } = useForm<Record<string, boolean>>({
    defaultValues: defaultFormValues
  });
  const { data } = useQuery({
    queryKey: [
      'board-member-experts-invite-list',
      { submissionContextId, submissionId }
    ],
    queryFn: () =>
      submissionId && submissionContextId
        ? fetchExpertsInviteDetails(
            submissionContextId,
            submissionId,
            'early-assessments'
          )
        : null
  });
  useEffect(() => {
    if (initialAssessmentData?.caseData) {
      reset({
        ...defaultFormValues,
        ...(initialAssessmentData?.caseData as Record<string, boolean>)
      });
    }
  }, [initialAssessmentData, reset]);

  const getChecklistCopy = useCallback(
    (fieldName: string): CheckListCopy | null => {
      const field = initialAssessmentFormSchema?.formFields?.find(
        field => field.name === fieldName
      );

      if (field) {
        const { name, params } = field;

        const normalizedName = getCheckListName(params?.labels?.main || name);
        return {
          heading: normalizedName,
          notesModalName: `${normalizedName} Note`,
          buttonText: `Add ${normalizedName} Note`
        };
      }

      return null;
    },
    [initialAssessmentFormSchema, getCheckListName]
  );

  const updateChecklist = async (name: string, value: boolean) => {
    const response = await updateChecklistMutation.mutateAsync({
      [name]: value
    });

    const checklistCopy = getChecklistCopy(name);
    if (response.isSuccess) {
      if (response.submission.workflowData.isAdminChecklistDone) {
        refetchSubmission();
      }
      return toast({
        variant: 'success',
        title: `${checklistCopy?.heading} ${value ? 'checked' : 'unchecked'}`,
        isClosable: true
      });
    } else {
      return toast({
        variant: 'error',
        title: ERRORS.somethingWentWrong,
        isClosable: true
      });
    }
  };
  const updateBulkChecklist = async () => {
    if (!initialAssessmentFormSchema) return;
    const fieldStatusMap = initialAssessmentFormSchema.formFields.reduce<
      Record<string, unknown>
    >((acc, field) => {
      acc[field.name] = true;
      return acc;
    }, {});

    const response = await updateChecklistMutation.mutateAsync(fieldStatusMap);

    if (response.isSuccess) {
      if (response.submission.workflowData.isAdminChecklistDone) {
        refetchSubmission();
      }
      return toast({
        variant: 'success',
        title: 'Task Checklist Completed',
        isClosable: true
      });
    } else {
      return toast({
        variant: 'error',
        title: ERRORS.somethingWentWrong,
        isClosable: true
      });
    }
  };

  const isWithdrawalRequested =
    !!workflowResponse.workflowData.withdrawal?.requested;
  const isUpdatedByAuthor =
    workflowResponse.workflowData.previousState === 'immediateRevision';

  const showHighlights = isUpdatedByAuthor || isWithdrawalRequested;
  const initialAssessmentStatus =
    workflowResponse.workflowData.currentState === 'initialAssessment';

  const renderCheckbox = (field: IEditorialFormField) => {
    const { name, component, params } = field;
    const { caseDataField, labels, validations } = params;

    const { buttonText } = getChecklistCopy(name) || {};

    const registerField = register(name);

    return (
      <Flex key={name} gap={2} flexDirection="column">
        <ProtectedAction
          relation="task_completer"
          entityType="journal_phase"
          entityId={`${ctxId}_initialAssessment`}
          tooltipLabel="You do not have permission to perform this action"
        >
          {component === 'Checkbox' ? (
            <>
              <Checkbox
                id={name}
                ariaLabel={labels.main}
                {...registerField}
                onChange={e => {
                  registerField.onChange(e);
                  updateChecklist(name, e.target.checked);
                }}
                value={
                  Utils.JsonUtil.extractByPath({
                    json: initialAssessmentData,
                    path: caseDataField
                  }) as string
                }
                isChecked={Boolean(
                  Utils.JsonUtil.extractByPath({
                    json: initialAssessmentData,
                    path: caseDataField
                  })
                )}
                isRequired={validations?.required}
                isDisabled={!isDecisionMaker}
              >
                <FieldLabel
                  isRequired={validations?.required}
                  label={labels.main}
                  subText={''}
                  helpText={labels.description}
                />
              </Checkbox>
              {buttonText && (
                <ButtonText
                  marginStart={7}
                  alignSelf="flex-start"
                  onClick={() => setShowAddNoteModal(name)}
                >
                  {buttonText}
                </ButtonText>
              )}
            </>
          ) : (
            <></>
          )}
        </ProtectedAction>
      </Flex>
    );
  };
  const ShowHighlightItem = () => {
    if (submissionContext?.config.isEarlyAssessmentEnabled) {
      return true;
    }
    if (showHighlights) {
      return true;
    }
    return false;
  };

  return (
    <AdditionalInformationWrapper>
      <Flex gap={2} flexDirection="column" width={'100%'}>
        <AddNoteModal
          isOpen={!!showAddNoteModal}
          onClose={() => setShowAddNoteModal(null)}
          onAdd={() => setShowAddNoteModal(null)}
          isLoading={false}
          name={
            showAddNoteModal
              ? getChecklistCopy(showAddNoteModal)?.notesModalName
              : undefined
          }
          submissionId={submissionId}
          submissionContextId={ctxId}
          checklistCategory={
            showAddNoteModal
              ? getChecklistCopy(showAddNoteModal)?.heading
              : undefined
          }
        />
        {ShowHighlightItem() && (
          <Accordion
            chakraAccordionProps={{
              width: '100%',
              defaultIndex: 0,
              allowToggle: true
            }}
            items={[
              {
                title: (
                  <Flex alignItems={'flex-start'} gap={2}>
                    <IconBadge icon={faBell} variant="neutral" />
                    <Flex flexDirection={'column'} marginLeft={2}>
                      <Text textStyle="headingItem">Highlights</Text>
                      <Text textStyle="textContext">
                        Review key updates and other relevant information in
                        this phase.
                      </Text>
                    </Flex>
                  </Flex>
                ),
                content:
                  submissionContext?.config.isEarlyAssessmentEnabled &&
                  initialAssessmentStatus &&
                  !showHighlights ? (
                    <Flex flexDirection="column" width="100%" gap={4}>
                      <HighlightItem
                        variant="attention"
                        icon={faEnvelope}
                        heading={`${data?.total ?? '0'} Experts Invited`}
                        context="Experts can be brought in to provide a recommendation
                   on the submission's suitability."
                        customButton={
                          <ProtectedWorkflowAction actionName="RequestEarlyAssessment">
                            <ButtonText
                              flexShrink="0"
                              onClick={() => {
                                setActiveTab(SUBMISSION_DETAIL_TABS.REVIEWS);
                                setReviewsSubTab('Early Assessments');
                              }}
                            >
                              Invite Experts
                            </ButtonText>
                          </ProtectedWorkflowAction>
                        }
                      />
                      <Divider />
                      {data && data?.total !== 0 && (
                        <>
                          {data?.completedAssessment > 0 && (
                            <HighlightItem
                              variant="success"
                              icon={faCheck}
                              heading={`${
                                data?.completedAssessment ?? '0'
                              } Early Assessment Done`}
                              buttonOnClick={() =>
                                setActiveTab(SUBMISSION_DETAIL_TABS.REVIEWS)
                              }
                              buttonText="To Reviews"
                            />
                          )}
                          {data?.declinedOrExpired > 0 && (
                            <>
                              <Divider />
                              <HighlightItem
                                variant="caution"
                                icon={faWarning}
                                heading={'Deadline Alerts'}
                                context={`0 Deadline in Early Assessment needs your attention`}
                                buttonOnClick={() =>
                                  setActiveTab(SUBMISSION_DETAIL_TABS.REVIEWS)
                                }
                                buttonText="To Reviews"
                              />
                            </>
                          )}
                        </>
                      )}
                    </Flex>
                  ) : (
                    <Flex flexDirection="column" width="100%" gap={4}>
                      {isUpdatedByAuthor && (
                        <HighlightItem
                          variant="attention"
                          icon={faFileImport}
                          context="Updates are now available to review in the
                       “Submission” and “About” tabs."
                          heading="Submission Updated by Author"
                        />
                      )}
                      <EditorialAuthorWithdrawalRequested />
                    </Flex>
                  )
              }
            ]}
          />
        )}
        {(initialAssessmentFormSchema?.formFields || []).length > 0 && (
          <Accordion
            chakraAccordionProps={{
              width: '100%',
              defaultIndex: 0,
              allowToggle: true
            }}
            items={[
              {
                icon: (
                  <Badge size="context" variant="neutral">
                    <FontAwesomeIcon icon={faPencil} />
                  </Badge>
                ),
                accordionItemProps: { marginBottom: 0 },
                title: 'Task Checklist',
                content: (
                  <Flex flexDirection="column" gap={2}>
                    <Text textStyle="textContext">
                      Ensure all required steps are completed before advancing
                      in the submission process.
                    </Text>
                    <Flex justifyContent="space-between" alignItems="center">
                      <Text>CHECKLIST</Text>
                      {isDecisionMaker && (
                        <ProtectedAction
                          relation="task_completer"
                          entityType="journal_phase"
                          entityId={`${ctxId}_initialAssessment`}
                          tooltipLabel="You do not have permission to perform this action"
                        >
                          <Button
                            variant="secondary"
                            width="50%"
                            onClick={updateBulkChecklist}
                            disabled={isCompleteChecklistDisabled}
                          >
                            Complete Checklist
                          </Button>
                        </ProtectedAction>
                      )}
                    </Flex>

                    <form>
                      <Flex gap={4} flexDirection="column">
                        {initialAssessmentFormSchema?.formFields?.map(
                          (field: IEditorialFormField) => renderCheckbox(field)
                        )}
                      </Flex>
                    </form>
                  </Flex>
                )
              }
            ]}
          />
        )}
      </Flex>
    </AdditionalInformationWrapper>
  );
};

export default withAuthorizedComponent(InitialAssessmentAdditionalInfo, {
  relation: 'task_completer',
  entityType: 'journal_phase',
  getEntityId: () => `${useCurrentContextId() ?? ''}_initialAssessment`
});
